/**
 * Branded full-page status shell (404, forbidden, app error, unavailable).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@components/Icon';
import type { IconType } from '@design-tokens/icons';
import { Logo } from '@components/Logo';
import { Button } from '@components/common/Button';
import { buildClassName } from '@utils';

import './AppStatusPage.css';

export interface AppStatusPageAction {
  label: string;
  to?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
}

export interface AppStatusPageProps {
  title: string;
  message: ReactNode;
  iconType?: IconType;
  iconColorVariant?: 'blue' | 'gray' | 'orange' | 'red';
  primaryAction?: AppStatusPageAction;
  secondaryAction?: AppStatusPageAction;
  /** Optional support code (app crash, etc.). */
  errorCode?: string;
  className?: string;
}

/** One action: a router link styled as a button when `to` is set, otherwise a Button. */
function StatusPageActionControl({
  action,
  defaultVariant,
}: {
  action: AppStatusPageAction;
  defaultVariant: NonNullable<AppStatusPageAction['variant']>;
}) {
  const variant = action.variant ?? defaultVariant;
  if (action.to) {
    return (
      <Link to={action.to} className={`bhg-button bhg-button--${variant} bhg-button--md`}>
        <span className="bhg-button__content">{action.label}</span>
      </Link>
    );
  }
  return (
    <Button type="button" variant={variant} onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

export function AppStatusPage({
  title,
  message,
  iconType = 'exclamation-circle',
  iconColorVariant = 'orange',
  primaryAction,
  secondaryAction,
  errorCode,
  className = '',
}: AppStatusPageProps) {
  return (
    <div className={buildClassName('bhg-app-status-page', className)}>
      <div className="bhg-app-status-page__card">
        <div className="bhg-app-status-page__logo" aria-hidden>
          <Logo size="medium" showText />
        </div>
        {iconType ? (
          <div className="bhg-app-status-page__icon" aria-hidden>
            <Icon
              type={iconType}
              size="xl"
              style="outlined"
              colorVariant={iconColorVariant}
              backgroundShape="circle"
            />
          </div>
        ) : null}
        <h1 className="bhg-app-status-page__title">{title}</h1>
        <div className="bhg-app-status-page__message">{message}</div>
        {errorCode ? (
          <p className="bhg-app-status-page__code">
            Reference: <span className="bhg-app-status-page__code-value">{errorCode}</span>
          </p>
        ) : null}
        <div className="bhg-app-status-page__actions">
          {primaryAction ? (
            <StatusPageActionControl action={primaryAction} defaultVariant="primary" />
          ) : null}
          {secondaryAction ? (
            <StatusPageActionControl action={secondaryAction} defaultVariant="outline" />
          ) : null}
        </div>
      </div>
    </div>
  );
}
